import { HTTP } from '@utils/http';
import { createContext, useContext, useState } from 'react';
import { MindbodyUser } from 'types/client';

interface FirebaseUser {
  uid: string;
  email: string;
  mindbodyInfo: MindbodyUser;
}

interface LoginResponse {
  user: FirebaseUser;
  // ... autres propriétés renvoyées par l'endpoint
}

interface RegisterResponse {
  user: FirebaseUser;
  // ... autres propriétés renvoyées par l'endpoint
}

export interface RegisterData {
  FirstName: string;
  LastName: string;
  AddressLine1: string;
  City: string;
  PostalCode: string;
  Country: string;
  BirthDate: string;
  MobilePhone: string;
  Email: string;
  Password: string;
  ReferredBy: string;
  Newsletter: boolean;
  TermsAndConditions: boolean;

  ReferralClientId?: number;

  CustomFields?: {
    ShoeSize?: number;
  };
}

interface IUserContext {
  isLoggedIn?: boolean;
  user?: MindbodyUser;
  setUser(user: MindbodyUser): void;
  isInitializing?: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  signUp: (data: RegisterData) => Promise<void>;
}

interface UserProviderProps {
  children: React.ReactNode;
}

const UserContext = createContext({} as IUserContext);

function UserProvider({ children }: UserProviderProps) {
  const { user: mindbodyUser } = mondayMindbody ?? {}; // Si les infos en session, alors on set le défaut avec ce qu'on a dans le WP
  const [user, setUser] = useState<MindbodyUser | undefined>(mindbodyUser);

  const login = async (email: string, password: string) => {
    const data = await HTTP.post<LoginResponse>('/auth/login', {
      email,
      password,
    }).send();
    setUser(data.user.mindbodyInfo);
  };

  const logout = async (): Promise<void> => {
    try {
      await HTTP.post('/auth/logout').send();
      setUser(undefined);
    } catch (error) {
      console.error('Sign up error:', error instanceof Error ? error.message : error);
    }
  };

  const signUp = async (data: RegisterData): Promise<void> => {
    const response = await HTTP.post<RegisterResponse>('/auth/register', data).send();
    setUser(response.user.mindbodyInfo);
  };

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        isLoggedIn: !!user,
        login,
        logout,
        signUp,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

function useUserContext() {
  const context = useContext(UserContext);
  if (context === undefined)
    throw new Error('You must wrap your React tree with <UserProvider /> in order to use useUserContext()');
  return context;
}

export { UserProvider, useUserContext };
